import React, { ElementRef, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import { FilterWrapperListClassName } from '../../../../components/FiltersWrapper';
import { FilterButtonClassName } from '../../../../components/MatchBetFilter';
import { StoppageTimeAnnouncedClassName } from '../../../../components/MatchCardHeader';
import { MainCardBadgeClassName } from '../../../../components/MatchCardHeaderBadge';
import {
  FullMatchTitleClassName,
  MatchHeaderClassName,
  MatchScoreboardWidgetClassName,
} from '../../../../components/MatchHeader/MatchHeader';
import MatchHeaderContainer from '../../../../components/MatchHeader/MatchHeaderContainer';
import StickyMatchActionsRowContainer from '../../../../components/StickyMatchActionsRow/StickyMatchActionsRowContainer';
import Tabs, { TabsSelectedClassName } from '../../../../components/Tabs';
import WinaLoader from '../../../../components/WinaLoader';
import { StickyMatchActionsRowContexts } from '../../../../constants/match';
import { usePrevious } from '../../../../hooks';
import useRouter from '../../../../hooks/useRouter';
import theme from '../../../../styled-components/dark';
import { type MatchStatus } from '../../../../types/match';
import { MatchViewModes, type MatchViewMode } from '../../../../types/matchView';
import { SportIds, type SportId } from '../../../../types/sports';
import { MatchBetFilter } from '../../../../utils/contexts';
import isLineUp from '../../../../utils/routes/isLineUp';
import { LineUpBackgroundsBySportId } from '../../assets/img';
import { SportsWithRaceTrackUI } from '../../constants/runtime';
import { LineUpMainViewStyleBySportId } from '../../constants/ui';
import { useLineUpInitialContext } from '../../contexts/LineUpInitialContext';
import { LineUpMainViewContext } from '../../contexts/LineUpMainViewContext';
import LineUpFieldContainer from '../LineUpField/LineUpFieldContainer';
import LineUpRaceTrackContainer from '../LineUpRaceTrack/LineUpRaceTrackContainer';
import LineUpSlider from '../LineUpSlider/LineUpSlider';
import LineUpSubstitutesListContainer from '../LineUpSubstitutesList/LineUpSubstitutesListContainer';

const SPACING = 0.75; // em

const RENDERING_TRIGGER_DELAY = 300; // ms
const APPEAR_TRANSITION_DURATION = 750; // ms

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

type WrapperType = {
  $isMMLoading: boolean;
  $matchViewMode: MatchViewMode;
  $sportId: SportId;
  $isLoading: boolean;
};

const Wrapper = styled.div<WrapperType>`
  position: relative;

  height: 100%;
  width: 100%;

  overflow: hidden scroll;

  ${({ $isMMLoading, $matchViewMode }) =>
    $matchViewMode === MatchViewModes.MY_MATCH
      ? css`
          --OddButton-valueOpacity: ${$isMMLoading ? 0 : 1};
          --OddButton-loaderOpacity: ${$isMMLoading ? 1 : 0};

          ${$isMMLoading
            ? css`
                --OddButton-pointerEvents: none;
              `
            : undefined}
        `
      : undefined}

  ${({ $sportId }) => LineUpMainViewStyleBySportId[$sportId]}

  ${({ $isLoading }) => {
    if ($isLoading) {
      return css`
        background-color: #000;
      `;
    }
    return css`
      background-image: var(--LineUpMainView-backgroundGradient);
    `;
  }}
  ${!Target.__MOBILE__
    ? css`
        &::-webkit-scrollbar {
          background-color: transparent;
          width: 0.6em;
        }

        &::-webkit-scrollbar-thumb {
          border-radius: 1em;
          background-color: ${theme.LineUpMainViewScrollbar.thumbColor};
        }

        &::-webkit-scrollbar-track-piece {
          border-radius: 1em;
        }

        &::-webkit-scrollbar-corner {
          background-color: transparent;
        }
      `
    : css`
        padding-bottom: constant(safe-area-inset-bottom);
        padding-bottom: env(safe-area-inset-bottom);
      `}
`;

type ContentWrapperType = {
  $lineUpBackground: string;
};

const ContentWrapper = styled.div<ContentWrapperType>`
  background-color: transparent;
  min-height: 100%;
  width: 100%;
  transform-style: preserve-3d;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  ${({ $lineUpBackground }) => {
    if (Target.__MOBILE__) {
      return css`
        background-size: cover;
        background-size: var(--LineUpMainView-mobileBackgroundSize);
        background-position: center;
        background-position: var(--LineUpMainView-mobileBackgroundPosition);

        background-image: url(${$lineUpBackground});
        background-repeat: no-repeat;
      `;
    }

    return css`
      background-size: cover;
      background-size: var(--LineUpMainView-backgroundSize);
      background-position: center;
      background-position: var(--LineUpMainView-backgroundPosition);

      background-image: url(${$lineUpBackground});
      background-repeat: no-repeat;
    `;
  }}

  width: 100%;

  &.line-up-main-view-content-transitions-enter {
    animation: ${appearAnim} ${APPEAR_TRANSITION_DURATION}ms ease-out forwards;
  }

  &.line-up-main-view-content-transitions-exit {
    animation: ${appearAnim} ${APPEAR_TRANSITION_DURATION}ms ease-out forwards reverse;
  }
`;

const StyledWinaLoader = styled(WinaLoader)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;

  &.line-up-main-view-loader-transitions-enter {
    animation: ${appearAnim} ${APPEAR_TRANSITION_DURATION}ms ease-out forwards;
  }

  &.line-up-main-view-loader-transitions-exit {
    animation: ${appearAnim} ${APPEAR_TRANSITION_DURATION}ms ease-out forwards reverse;
  }
`;

type StyledMatchHeaderContainerType = {
  $sportId: SportId;
  $matchViewMode: MatchViewMode;
};

const StyledMatchHeaderContainer = styled(MatchHeaderContainer)<StyledMatchHeaderContainerType>`
  z-index: 1;
  margin: ${SPACING}em ${SPACING}em 0.5em;
  width: calc(100% - 2 * ${SPACING}em);
  ${({ $sportId }) => {
    if (SportsWithRaceTrackUI.indexOf($sportId) !== -1) {
      return css`
        --MatchHeader-dynamicDefaultBackgroundColor: ${theme.LineUpMatchHeader.darkBackground};
        --MatchHeader-cardBackgroundColor: ${theme.LineUpMatchHeader.darkHeaderBackground};
        --MatchHeader-headerBackgroundColor: transparent;
      `;
    }

    return css`
      --MatchHeader-dynamicDefaultBackgroundColor: ${theme.LineUpMatchHeader.defaultTransparentBackground};
      --MatchHeader-cardBackgroundColor: ${theme.LineUpMatchHeader.backgroundColor};
      --MatchHeader-headerBackgroundColor: ${theme.LineUpMatchHeader.transparentHeaderBackground};
    `;
  }}

  ${({ $matchViewMode }) => css`
    ${$matchViewMode === MatchViewModes.MY_MATCH
      ? css`
          & .${MainCardBadgeClassName} {
            background-color: #2a151a;
          }
        `
      : css`
          & .${MainCardBadgeClassName} {
            background-color: var(--MatchHeader-cardBackgroundColor);
          }
        `}
  `}

  & .${MatchHeaderClassName} {
    background-color: var(--MatchHeader-headerBackgroundColor);
  }

  & .${MatchScoreboardWidgetClassName} {
    background-color: transparent;
  }

  & .${StoppageTimeAnnouncedClassName} {
    background-color: ${theme.LineUpMatchHeader.backgroundColor};
  }

  & .${FullMatchTitleClassName} {
    background-color: transparent;
  }
`;

type StyledStickyMatchActionsRowContainerType = {
  $hasMinHeight: boolean;
};

const StyledStickyMatchActionsRowContainer = styled(
  StickyMatchActionsRowContainer,
)<StyledStickyMatchActionsRowContainerType>`
  margin: 0 ${SPACING}em 0.5em;
  padding: 0;
  width: calc(100% - 2 * ${SPACING}em);

  ${({ $hasMinHeight }) => css`
    min-height: ${$hasMinHeight ? '3em' : '0em'};
  `}

  position: relative;
  top: unset;
  z-index: unset;
  transform: unset;

  ${Target.__MOBILE__
    ? css`
        --match-bet-filters-main-background: transparent;
      `
    : undefined};

  & .${FilterButtonClassName} {
    background-color: transparent;
  }

  & .${FilterWrapperListClassName} {
    padding: 0;
  }

  --MatchViewModeSwitch-dynamicDefaultBackgroundColor: rgba(255, 255, 255, 0.1);
  --BScrollViewShadow-backgroundColor: #0a1034;
`;

const StyledLineUpSlider = styled(LineUpSlider)`
  transform: translateZ(1px);
  width: 100%;
`;

const TeamSelectorWrapper = styled.div`
  padding: 0 ${SPACING}em;
  margin-bottom: ${SPACING}em;
  width: 100%;
`;

const TeamSelector = styled(Tabs)`
  & .${TabsSelectedClassName} {
    margin: 0.1em;
    height: 1.8em;
    width: calc(50% - 0.1em);
  }
`;

const StyledLineUpFieldContainer = styled(LineUpFieldContainer)`
  ${() => {
    if (Target.__MOBILE__) {
      return css`
        width: 100%;
        font-size: 100%;
      `;
    }
    return css`
      width: var(--LineUpMainView-desktopFieldWidth);
      font-size: var(--LineUpMainView-desktopFontSize);
    `;
  }}
`;

type LineUpFieldWrapperType = {
  $preventPadding: boolean;
};

const LineUpFieldWrapper = styled.div<LineUpFieldWrapperType>`
  width: 100%;
  flex: 1;

  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ $preventPadding }) =>
    $preventPadding
      ? css`
          padding: 0;
        `
      : css`
          padding: 0 ${SPACING}em;
        `}
`;

const StyledLineUpSubstitutesListContainer = styled(LineUpSubstitutesListContainer)`
  width: 100%;
  margin-top: -1em;

  ${Target.__MOBILE__
    ? css`
        font-size: 100%;
      `
    : css`
        font-size: var(--LineUpMainView-desktopFontSize);
      `}
`;

type TabType = { id: string; icon?: ReactNode; label?: string; disabled?: boolean };

const findClosestPossibleTeamId = (tabs: Array<TabType>, defaultTeamId?: number): number | null => {
  if (defaultTeamId) {
    if (defaultTeamId === 1) {
      if (tabs[0]?.disabled) {
        if (tabs[1]?.disabled) {
          return null;
        }
        return 2;
      }
      return 1;
    }

    if (defaultTeamId === 2) {
      if (tabs[1]?.disabled) {
        if (tabs[0]?.disabled) {
          return null;
        }
        return 1;
      }
      return 2;
    }
  }

  const firstAvailableTabIndex = tabs.findIndex((t) => !t.disabled);

  return firstAvailableTabIndex !== -1 ? firstAvailableTabIndex + 1 : null;
};

type LineUpMainViewProps = {
  className?: string;
  tabs: Array<{ id: string; label: string; disabled: boolean }>;
  filters: Array<MatchBetFilter>;
  matchId: number;
  matchStatus?: MatchStatus;
  sportId: SportId;
  matchViewMode: MatchViewMode;
  isMMLoading: boolean;
};

const LineUpMainView = ({
  className = '',
  tabs,
  filters,
  matchId,
  matchStatus,
  sportId,
  matchViewMode,
  isMMLoading,
}: LineUpMainViewProps) => {
  const [shouldRenderContent, setShouldRenderContent] = useState(false);
  const contentRenderingTimeoutRef = useRef<NodeJS.Timeout | null>();
  const { defaultTeamId, defaultFilterId } = useLineUpInitialContext();
  const [teamId, setTeamId] = useState(findClosestPossibleTeamId(tabs, defaultTeamId));
  const [filterId, setFilterId] = useState<string | null>(defaultFilterId ?? filters[0]?.id ?? null);
  const previousFilterId = usePrevious(filterId);
  const [sliderValue, setSliderValue] = useState<string | undefined>(undefined);
  const filtersRef = useRef<Array<MatchBetFilter>>();
  const wrapperRef = useRef<ElementRef<'div'>>(null);

  const onValueChange = useCallback((v: undefined | string) => {
    setSliderValue(v);
  }, []);

  const {
    location: { pathname },
  } = useRouter();

  useEffect(() => {
    const defaultSliderValues = filters?.find((filter) => filter.id === filterId)?.sliderValues;
    if (filterId && defaultSliderValues) {
      if (previousFilterId !== filterId) {
        setSliderValue(defaultSliderValues[0].value);
      }
    } else {
      setSliderValue(undefined);
    }
  }, [previousFilterId, filterId, filters]);

  const sliderValues = useMemo(
    () => filters?.find((filter) => filter.id === filterId)?.sliderValues ?? [],
    [filterId, filters],
  );

  useEffect(() => {
    const isPathnameMatchingLineUpPattern = isLineUp(pathname);
    const wrapperNode = wrapperRef.current;

    if (isPathnameMatchingLineUpPattern && wrapperNode) {
      wrapperNode.scrollTop = 0;
    }
  }, [pathname]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (Array.isArray(tabs)) {
      setTeamId(findClosestPossibleTeamId(tabs, defaultTeamId));
    }
  }, [defaultTeamId, tabs]);

  useEffect(() => {
    setFilterId(defaultFilterId ?? filtersRef.current?.[0]?.id ?? null);
  }, [defaultFilterId]);

  const onTeamSelectionChanges = useCallback((teamId: string | number) => {
    setTeamId(typeof teamId === 'string' ? parseInt(teamId, 10) : teamId);
  }, []);

  const onFilterSelectionChanges = useCallback(
    (filter: string | null) => {
      setFilterId(filter ? filters.find((f) => f.id === filter)?.id ?? null : null);
    },
    [filters],
  );

  useEffect(() => {
    setFilterId((prev) => {
      if (prev) {
        if (filters.find((f) => f.id === prev)) {
          return prev;
        }
        if (defaultFilterId && filters.find((f) => f.id === defaultFilterId)) {
          return defaultFilterId;
        }
      }

      if (defaultFilterId && filters.find((f) => f.id === defaultFilterId)) {
        return defaultFilterId;
      }

      return filters[0]?.id ?? null;
    });
  }, [filters, defaultFilterId]);

  useEffect(() => {
    if (matchId) {
      contentRenderingTimeoutRef.current = setTimeout(() => {
        setShouldRenderContent(true);
        contentRenderingTimeoutRef.current = null;
      }, RENDERING_TRIGGER_DELAY);
    } else {
      setShouldRenderContent(false);
    }
  }, [matchId]);

  useEffect(() => {
    return () => {
      if (contentRenderingTimeoutRef.current) {
        clearTimeout(contentRenderingTimeoutRef.current);
        contentRenderingTimeoutRef.current = null;
      }
    };
  }, []);

  const renderTeamSelector = useMemo(() => !!tabs?.length && teamId, [tabs, teamId]);

  const forwardedState = useMemo(
    () => ({ matchId, sportId, teamId, filterId, sliderValue, matchStatus }),
    [matchId, sportId, teamId, filterId, sliderValue, matchStatus],
  );

  const preventFieldPadding = useMemo(() => sportId === SportIds.RUGBY || sportId === SportIds.RUGBY_LEAGUE, [sportId]);

  const lineUpBackground = useMemo(() => LineUpBackgroundsBySportId[sportId], [sportId]);

  const displayTabs = useMemo(
    () => sportId !== SportIds.BASKETBALL && SportsWithRaceTrackUI.indexOf(sportId) === -1,
    [sportId],
  );

  const displaySlider = useMemo(() => sliderValues && sliderValues.length > 0, [sliderValues]);

  const renderContent = useMemo(() => {
    if (SportsWithRaceTrackUI.indexOf(sportId) !== -1) {
      return (
        <LineUpFieldWrapper $preventPadding={preventFieldPadding}>
          <LineUpRaceTrackContainer />
        </LineUpFieldWrapper>
      );
    }
    return (
      <>
        {displaySlider ? (
          <StyledLineUpSlider values={sliderValues} currentValue={sliderValue} onValueChange={onValueChange} />
        ) : null}
        {renderTeamSelector && teamId ? (
          <TeamSelectorWrapper>
            <TeamSelector
              tabs={tabs}
              onSelectionChange={onTeamSelectionChanges}
              selected={`${teamId}`}
              tabHeight={2}
              customStyle={{
                defaultBgColor: theme.LineUpMatchHeader.defaultTransparentBackground,
                defaultColor: theme.DefaultTabs.default.color,
                selectedBgColor: theme.LineUpMatchHeader.selected.backgroundColor,
                selectedColor: theme.DefaultTabs.selected.color,
              }}
            />
          </TeamSelectorWrapper>
        ) : null}
        <LineUpFieldWrapper $preventPadding={preventFieldPadding}>
          <StyledLineUpFieldContainer />
        </LineUpFieldWrapper>
        <StyledLineUpSubstitutesListContainer />
      </>
    );
  }, [
    displaySlider,
    onTeamSelectionChanges,
    onValueChange,
    preventFieldPadding,
    renderTeamSelector,
    sliderValue,
    sliderValues,
    sportId,
    tabs,
    teamId,
  ]);

  return (
    <Wrapper
      className={className}
      ref={wrapperRef}
      $isLoading={!shouldRenderContent || (typeof teamId !== 'number' && displayTabs)}
      $matchViewMode={matchViewMode}
      $isMMLoading={isMMLoading}
      $sportId={sportId}
    >
      <CSSTransition
        in={shouldRenderContent && (typeof teamId === 'number' || !displayTabs)}
        timeout={APPEAR_TRANSITION_DURATION}
        classNames="line-up-main-view-content-transitions"
        mountOnEnter
        unmountOnExit
      >
        <ContentWrapper $lineUpBackground={lineUpBackground}>
          <LineUpMainViewContext.Provider value={forwardedState}>
            <StyledMatchHeaderContainer
              $sportId={sportId}
              matchId={matchId}
              $matchViewMode={matchViewMode}
              simplified
              disableScroll
            />
            <StyledStickyMatchActionsRowContainer
              $hasMinHeight={filters?.length > 1}
              matchId={matchId}
              filters={filters}
              onFilterChanged={onFilterSelectionChanges}
              defaultFilterId={defaultFilterId}
              untoggleable
              noAutoScroll
              context={StickyMatchActionsRowContexts.LINE_UP}
            />
            {renderContent}
          </LineUpMainViewContext.Provider>
        </ContentWrapper>
      </CSSTransition>
      <CSSTransition
        in={!shouldRenderContent || (typeof teamId !== 'number' && displayTabs)}
        timeout={APPEAR_TRANSITION_DURATION}
        classNames="line-up-main-view-loader-transitions"
        mountOnEnter
        unmountOnExit
      >
        <StyledWinaLoader />
      </CSSTransition>
    </Wrapper>
  );
};

export default LineUpMainView;
